/** @jsxImportSource @emotion/react */
import React, {
  ReactNode,
  Children,
  cloneElement,
  isValidElement,
} from "react";
import ToggleButton from "components/atoms/ToggleButton.atom";
import { type ToggleButtonProps } from "components/atoms/ToggleButton.atom";

export type ToggleButtonGroupBaseProps = {
  disabled?: boolean;
  children: ReactNode;
  size?: "small" | "medium" | "large";
  className?: string;
  css?: React.CSSProperties;
};

export type ToggleButtonGroupExclusiveProps = ToggleButtonGroupBaseProps & {
  exclusive: true;
  value: string | null;
  onChange: (value: string | null) => void;
};

export type ToggleButtonGroupNonExclusiveProps = ToggleButtonGroupBaseProps & {
  exclusive?: false;
  value: string[];
  onChange: (value: string[]) => void;
};

export type ToggleButtonGroupProps =
  | ToggleButtonGroupExclusiveProps
  | ToggleButtonGroupNonExclusiveProps;

const isExclusive = (
  props: ToggleButtonGroupProps,
): props is ToggleButtonGroupExclusiveProps => props.exclusive === true;

export const ToggleButtonGroup: React.FC<ToggleButtonGroupProps> = (props) => {
  const { disabled = false, children, size = "medium", className, css } = props;

  const handleClick = (childValue: string) => {
    if (isExclusive(props)) {
      if (props.value === childValue) {
        props.onChange(null);
      } else {
        props.onChange(childValue);
      }
    } else {
      const valueArray = props.value;
      if (valueArray.includes(childValue)) {
        props.onChange(valueArray.filter((v) => v !== childValue));
      } else {
        props.onChange([...valueArray, childValue]);
      }
    }
  };

  return (
    <div className={className} style={{ display: "inline-flex", ...css }}>
      {Children.toArray(children).map((child, idx, arr) => {
        if (!isValidElement(child)) {
          return null;
        }
        const btn = child as React.ReactElement<ToggleButtonProps>;
        const childValue = btn.props.value;
        let selected: boolean;
        if (isExclusive(props)) {
          selected = props.value === childValue;
        } else {
          selected = props.value.includes(childValue);
        }
        const isFirst = idx === 0;
        const isLast = idx === arr.length - 1;
        const borderRadius = isFirst
          ? `${
              btn.props.size === "small"
                ? 6
                : btn.props.size === "large"
                ? 12
                : 8
            }px 0 0 ${
              btn.props.size === "small"
                ? 6
                : btn.props.size === "large"
                ? 12
                : 8
            }px`
          : isLast
          ? `0 ${
              btn.props.size === "small"
                ? 6
                : btn.props.size === "large"
                ? 12
                : 8
            }px ${
              btn.props.size === "small"
                ? 6
                : btn.props.size === "large"
                ? 12
                : 8
            }px 0`
          : "0";
        const groupCss = {
          borderRadius,
          marginLeft: isFirst ? 0 : -1,
        };
        const mergedCss = btn.props.css ? [btn.props.css, groupCss] : groupCss;
        const groupOnClick = () => handleClick(childValue);
        const childOnClick = btn.props.onClick;
        const composedOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
          groupOnClick();
          if (childOnClick) {
            childOnClick(e);
          }
        };
        return cloneElement(btn, {
          selected,
          disabled: disabled || btn.props.disabled,
          size,
          css: mergedCss,
          onClick: composedOnClick,
        });
      })}
    </div>
  );
};

export default ToggleButtonGroup;
